import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterContentInit,
  ContentChildren,
  Directive,
  ElementRef,
  forwardRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  QueryList,
} from '@angular/core';
import {
  auditTime,
  fromEvent,
  merge,
  startWith,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';

import { ListItemDirective } from './list-item.directive';

export enum ListOrientation {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

@Directive({
  selector: '[pluginsList]',
  standalone: true,
  host: {
    '(keydown)': 'handleKeydown($event)',
    '(focusout)': 'handleFocusOut()',
  },
  exportAs: 'pluginsList',
})
export class ListDirective implements AfterContentInit, OnDestroy {
  private readonly element = inject(ElementRef).nativeElement as HTMLElement;
  private readonly ngZone = inject(NgZone);

  handleKeydown(event: KeyboardEvent) {
    if (!this.keyManager || this.keyManager.activeItemIndex === null) return;
    this.keydownCallback?.(event, this.keyManager.activeItemIndex);
    this.keyManager.onKeydown(event);
  }

  handleFocusOut() {
    this.previousActiveItem = this.keyManager?.activeItem ?? null;
  }
  @Input('pluginsListWrap') set wrap(value: BooleanInput) {
    this._wrap = coerceBooleanProperty(value);
    this.keyManager?.withWrap(this._wrap);
  }
  get wrap(): boolean {
    return this._wrap;
  }
  private _wrap = false;

  @Input('pluginsListOrientation') set orientation(value: ListOrientation) {
    if (value === ListOrientation.HORIZONTAL) {
      this._orientation = ListOrientation.HORIZONTAL;
      this.keyManager?.withHorizontalOrientation('ltr');
    } else {
      this._orientation = ListOrientation.VERTICAL;
      this.keyManager?.withVerticalOrientation();
    }
  }
  get orientation() {
    return this._orientation;
  }
  private _orientation: ListOrientation = ListOrientation.VERTICAL;

  @Input('pluginsListRefocus') refocus = true;

  @Input() set pluginsListActiveItemIndex(val: number) {
    this.activeItemIndex = val;
    const listItem = this.getListItem(val);
    if (listItem) this.setActiveItem(listItem);
  }

  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('pluginsListOnKeydown') keydownCallback?: (
    event: KeyboardEvent,
    activeIndex: number,
  ) => void;

  // eslint-disable-next-line @angular-eslint/no-forward-ref
  @ContentChildren(forwardRef(() => ListItemDirective), { descendants: true })
  private items!: QueryList<ListItemDirective>;

  private keyManager?: ActiveDescendantKeyManager<ListItemDirective>;
  private previousActiveItem: ListItemDirective | null = null;
  private activeItemIndex = NaN;
  private readonly itemOrderChange$ = new Subject<void>();
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.handleWindowBlur();
  }

  ngAfterContentInit() {
    merge(
      this.items.changes.pipe(startWith(this.items)),
      this.itemOrderChange$.pipe(),
    )
      .pipe(
        auditTime(50),
        tap(() => this.initKeyManager()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  selectFocusedItem() {
    const active = this.keyManager?.activeItem;
    if (!active) return;

    active.selectItem();
  }

  focusNext() {
    this.keyManager?.setNextItemActive();
  }

  focusPrev() {
    this.keyManager?.setPreviousItemActive();
  }

  private initKeyManager() {
    this.keyManager?.destroy();
    const items =
      this.items?.toArray().sort((a, b) => b.order - a.order) ??
      ([] as ListItemDirective[]);
    this.keyManager = new ActiveDescendantKeyManager(items)
      .withWrap(this.wrap)
      .withHomeAndEnd()
      .withAllowedModifierKeys(['shiftKey']);

    if (this.orientation === 'vertical') {
      this.keyManager.withVerticalOrientation();
    } else {
      this.keyManager.withHorizontalOrientation('ltr');
    }

    if (this.previousActiveItem) {
      this.setActiveItem(this.previousActiveItem);
    } else {
      const initialItem =
        items[this.activeItemIndex] ?? items.find((i) => i.initial) ?? items[0];
      this.setActiveItem(initialItem);
      initialItem?.detectChanges();
    }
    this.keyManager.change.subscribe(
      () => this.refocus && this.focusActiveItem(),
    );
  }

  onItemOrderChange() {
    this.itemOrderChange$.next();
  }

  private focusActiveItem() {
    this.keyManager?.activeItem?.focus();
  }

  setActiveItem(item: ListItemDirective) {
    this.keyManager?.setActiveItem(item);
  }

  isActive(item: ListItemDirective): boolean {
    return !!(this.keyManager?.activeItem === item);
  }

  getListItem(index: number) {
    return this.items?.get(index);
  }

  private handleWindowBlur() {
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'blur')
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (
            this.element.contains(document.activeElement) &&
            this.previousActiveItem
          ) {
            this.setActiveItem(this.previousActiveItem);
            this.previousActiveItem = null;
          }
        });
    });
  }

  ngOnDestroy() {
    this.keyManager?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
